import api from './api';

/** Shipment-backed analytics surfaced as Docket terminology in the UI. */
export interface DocketAnalyticsSummary {
  totalDockets: number;
  activeDockets: number;
  deliveredDockets: number;
  onTimeDeliveryRate: number;
  totalRevenue: number;
  pendingRevenue: number;
  avgDocketValue: number;
}

export interface DocketStatusChartItem {
  status: string;
  count: number;
  percentage?: number;
}

export interface DocketTypeChartItem {
  type: string;
  count: number;
}

export interface DocketAnalyticsDashboard {
  summary: DocketAnalyticsSummary;
  docketsByStatus: DocketStatusChartItem[];
  docketsByType: DocketTypeChartItem[];
}

export interface AnalyticsDateParams {
  startDate?: string;
  endDate?: string;
}

export interface EnterpriseKpis {
  todaysTrips: number;
  activeDockets: number;
  delayedDeliveries: number;
  fleetUtilization: number;
  vehicleAvailability: { available: number; total: number };
  driverAvailability: { available: number; onDuty: number; total: number };
  revenue: number;
  pendingBilling: number;
  monthlyTrends: Array<{ month: string; dockets: number; revenue: number }>;
}

export type GlobalSearchResult = {
  type: 'customer' | 'vehicle' | 'driver' | 'docket' | 'shipment' | 'invoice';
  id: string;
  label: string;
  meta?: string;
  status?: string;
};

/** Docket-native operational overview from GET /analytics/overview */
export interface AnalyticsOverview {
  summary: {
    totalDockets: number;
    openDockets: number;
    inTransit: number;
    delivered: number;
    pendingPod: number;
    invoiced: number;
    paid: number;
    closed: number;
  };
  revenue: {
    totalInvoices: number;
    outstandingAmount: number;
    collectedAmount: number;
  };
  charts: {
    docketsByStatus: Array<{ status: string; count: number }>;
    docketsByBranch: Array<{ branchId: string; branchName: string; docketCount: number }>;
  };
}

/** Execution dashboard shape derived from docket overview APIs */
export interface ExecutionAnalyticsDashboard {
  summary: AnalyticsOverview['summary'];
  revenue: AnalyticsOverview['revenue'];
  docketsByStatus: AnalyticsOverview['charts']['docketsByStatus'];
  docketsByBranch: AnalyticsOverview['charts']['docketsByBranch'];
}

export interface OpsDocketKpis {
  companyId: string;
  window: { start: string; end: string };
  docketsCreated: number;
  docketsPlacedOnTimeWindow: number;
  docketsDeliveredInWindow: number;
  onTimePlacementPct: number;
  deliveryAdherencePct: number;
}

interface DashboardApiResponse {
  summary: {
    totalShipments: number;
    activeShipments: number;
    deliveredShipments: number;
    onTimeDeliveryRate: number;
    totalRevenue: number;
    pendingRevenue: number;
    avgShipmentValue: number;
  };
  shipmentsByStatus: Array<{ status: string; count: number; percentage: number }>;
}

interface ShipmentMetricsApiResponse {
  shipmentsByType: Array<{ type: string; count: number }>;
}

const analyticsService = {
  /** Primary execution analytics — docket-native overview */
  async getExecutionOverview(params?: AnalyticsDateParams): Promise<AnalyticsOverview> {
    const { data } = await api.get<AnalyticsOverview>('/analytics/overview', { params });
    return data;
  },

  /** Maps overview into the execution dashboard chart shape */
  async getExecutionDashboard(params?: AnalyticsDateParams): Promise<ExecutionAnalyticsDashboard> {
    const overview = await this.getExecutionOverview(params);
    return {
      summary: overview.summary,
      revenue: overview.revenue,
      docketsByStatus: overview.charts.docketsByStatus,
      docketsByBranch: overview.charts.docketsByBranch,
    };
  },

  async getOpsDocketKpis(startDate: string, endDate: string): Promise<OpsDocketKpis> {
    const { data } = await api.get<OpsDocketKpis>('/analytics/ops/docket-kpis', {
      params: { startDate, endDate },
    });
    return data;
  },

  /** @deprecated Shipment-backed dashboard — use getExecutionDashboard for execution UI */
  async getDocketDashboard(params?: AnalyticsDateParams): Promise<DocketAnalyticsDashboard> {
    const query: Record<string, string> = {};
    if (params?.startDate) query.startDate = params.startDate;
    if (params?.endDate) query.endDate = params.endDate;

    const [dashboardRes, metricsRes] = await Promise.all([
      api.get<DashboardApiResponse>('/analytics/dashboard', { params: query }),
      api.get<ShipmentMetricsApiResponse>('/analytics/shipments', { params: query }),
    ]);

    const dashboard = dashboardRes.data;
    const metrics = metricsRes.data;

    return {
      summary: {
        totalDockets: dashboard.summary.totalShipments,
        activeDockets: dashboard.summary.activeShipments,
        deliveredDockets: dashboard.summary.deliveredShipments,
        onTimeDeliveryRate: dashboard.summary.onTimeDeliveryRate,
        totalRevenue: dashboard.summary.totalRevenue,
        pendingRevenue: dashboard.summary.pendingRevenue,
        avgDocketValue: dashboard.summary.avgShipmentValue,
      },
      docketsByStatus: dashboard.shipmentsByStatus.map((item) => ({
        status: item.status,
        count: item.count,
        percentage: item.percentage,
      })),
      docketsByType: metrics.shipmentsByType,
    };
  },

  async getEnterpriseKpis(): Promise<EnterpriseKpis> {
    const { data } = await api.get<EnterpriseKpis>('/analytics/enterprise-kpis');
    return data;
  },

  async globalSearch(q: string, page = 1, limit = 10) {
    const { data } = await api.get<{
      query: string;
      page: number;
      limit: number;
      total: number;
      results: GlobalSearchResult[];
    }>('/analytics/search', { params: { q, page, limit } });
    return data;
  },
};

export default analyticsService;
